import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { GrClose } from 'react-icons/gr';
import { useInView } from 'react-intersection-observer';
import { propTypesSliceHomepageWeb } from '../../../../templates/HomepageWeb/components/SliceZoneHomepageWeb/SliceZoneHomepageWeb.proptypes';
import Button, { ButtonAppearance } from '../../../../ui/Atoms/Button';
import Picture from '../../../../ui/Atoms/Picture';
import { hexToRGB } from '../../../utils/hexToRGB';

import * as S from './InteractiveVertical.styles.js';

const CustomModal = ({ children, onModalClose }) => {
  useEffect(() => {
    function keyListener(event) {
      if (event.keyCode === 27) {
        onModalClose();
      }
    }

    document.addEventListener('keydown', keyListener);

    return () => document.removeEventListener('keydown', keyListener);
  });

  return ReactDOM.createPortal(
    <S.ModalOverlay aria-modal='true' role='dialog'>
      <S.ModalWrapper>
        <S.StyledCross onClick={() => onModalClose()}>
          <GrClose />
        </S.StyledCross>
        <S.ModalContent>{children}</S.ModalContent>
      </S.ModalWrapper>
    </S.ModalOverlay>,
    document.body
  );
};

const Text = React.lazy(() => import('../../../../ui/Atoms/Text'));

export const InteractiveVerticalSlice = ({ slice }) => {
  const [idVisibleContent, setIdVisibleContent] = useState(0);
  const [inViewFirstTime, setInViewFirstTime] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const isClicked = useRef(false);

  const inViewChange = inView => {
    if (!inViewFirstTime && inView) {
      setInViewFirstTime(true);
    }
  };

  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0.1,
    onChange: inViewChange,
  });

  if (!slice) {
    return null;
  }

  const data = slice.primary || {};
  const {
    interactive_vertical_slice_id: id,
    interactive_vertical_cta_label: label,
    interactive_vertical_cta_datatrack_attribute: dataTrackAttribute,
    interactive_vertical_cta_link: { url: link } = {},
  } = data || '';

  const items = slice.items || {};

  if (items.length < 1) {
    return null;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleSliding = useCallback(() => {
    const length = items.length;
    if (idVisibleContent + 1 > length - 1) {
      setIdVisibleContent(0);
    } else {
      setIdVisibleContent(idVisibleContent + 1);
    }
  }, [items, idVisibleContent, setIdVisibleContent]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (inView) {
      const sliding = setTimeout(handleSliding, 20000);

      if (isClicked.current) {
        isClicked.current = false;
        clearTimeout(sliding);
      }

      return () => clearTimeout(sliding);
    }
  }, [handleSliding, inView]);

  return (
    <S.Wrapper id={id} isVisible={true} ref={ref}>
      {items.map((item, index) => {
        const {
          interactive_vertical_cta_illustration: { alt, url },
        } = item || '';

        return (
          <S.IllustrationWrapper
            isVisible={index === idVisibleContent}
            key={`${item}-${index}`}
          >
            <Picture
              altText={alt}
              imageUrl={url}
              isVisible={index === idVisibleContent}
              key={index}
            />
          </S.IllustrationWrapper>
        );
      })}
      <S.ListWrapper>
        {items.map((item, index) => {
          const {
            interactive_vertical_cta_title: itemTitle,
            description: { html: description },
            color,
          } = item || '';

          return (
            <React.Fragment key={`${item}-${index}`}>
              <S.Card
                color={hexToRGB(color, 0.1)}
                isVisible={index === idVisibleContent}
              >
                <S.ContentWrapper key={index}>
                  <S.ContentTitle key={index}>{itemTitle}</S.ContentTitle>
                  <React.Suspense fallback='Loading...'>
                    <Text key={index}>{description}</Text>
                  </React.Suspense>
                </S.ContentWrapper>
                <S.ProgessBar
                  color={color}
                  inView={inView}
                  isClicked={isClicked.current}
                  isVisible={index === idVisibleContent}
                />
              </S.Card>
              <S.CtaTitle
                isVisible={index !== idVisibleContent}
                onClick={() => {
                  setIdVisibleContent(index);
                  isClicked.current = true;
                }}
              >
                {itemTitle}
              </S.CtaTitle>
            </React.Fragment>
          );
        })}
        <S.ButtonContainer>
          <Button
            appearance={ButtonAppearance.OUTLINE}
            dataTrackAttribute={dataTrackAttribute}
            onClick={() => setIsModalVisible(true)}
          >
            {label}
          </Button>
        </S.ButtonContainer>
      </S.ListWrapper>
      {isModalVisible && link && (
        <CustomModal onModalClose={() => setIsModalVisible(false)}>
          <S.WrapperIframe>
            <S.StyledIframe
              allowfullscreen={true}
              frameborder='0'
              height='405'
              id='ytplayer'
              src={link}
              type='text/html'
              width='650'
            />
          </S.WrapperIframe>
        </CustomModal>
      )}
    </S.Wrapper>
  );
};

InteractiveVerticalSlice.propTypes = {
  /**
   * Content of the HeaderBasic slice.
   */
  slice: PropTypes.shape(propTypesSliceHomepageWeb),
};
